import { buildOpFairView, opLineCanPush, OP_PERIOD_WIRE, type OpFairView } from '@/lib/opOdds';
import type { OpOddsSlice } from '@/stores/marketStore';
import type { GameEventRow, SpreadLine, TotalLine } from '../ValueDashboard/types';
import { classifyLineAvailability } from '@/lib/sport/lineAvailability';
import { spreadLineKey, totalLineKey } from './synthesize';

export type ButterflyEventIdentity = Pick<GameEventRow, 'key' | 'awayCode' | 'homeCode'>;
export interface ButterflyReferenceReaders {
  markets?: (game: string) => OpOddsSlice[];
  spread: (game: string, home: string, line: number) => { slice: OpOddsSlice; swapped: boolean } | null;
  total: (game: string, line: number) => OpOddsSlice | null;
}

/**
 * THE admitted reference slice for one executable line in one scope of play
 * — the single path the Butterfly cards and the action row's comparison cell
 * both read, so a period card and its row cannot disagree (#884 review).
 *
 * Full game goes through the store's exact full-game getters (#893). A
 * period resolves the declared period bucket at the exact strike, from the
 * slice's own provider orientation: aligned (provider home = row home) ⇒
 * points = −rowLine, flipped ⇒ +rowLine. Never the opposite sign, never a
 * full-game bucket standing in for a period, never a period g1 knows but the
 * provider has no vocabulary for.
 */
export function resolveLineReferenceSlice(
  event: ButterflyEventIdentity,
  kind: 'spread' | 'total',
  line: SpreadLine | TotalLine,
  readers: ButterflyReferenceReaders,
  period: string = 'full',
): OpOddsSlice | null {
  if (period === 'full') {
    return kind === 'spread'
      ? readers.spread(event.key, event.homeCode, line.line)?.slice ?? null
      : readers.total(event.key, line.line);
  }
  const periodKey = OP_PERIOD_WIRE[period];
  if (periodKey == null) return null;
  return (
    readers.markets?.(event.key).find((s) => {
      if ((s.market.period ?? s.market.periodTypeId) !== periodKey || s.market.marketType !== kind) return false;
      if (kind === 'total') return s.market.points === line.line;
      if (s.awayTeam !== event.awayCode && s.awayTeam !== event.homeCode) return false;
      return s.market.points === (s.awayTeam === event.homeCode ? line.line : -line.line);
    }) ?? null
  );
}

/** The admitted slice as a row-oriented fair view (spreads align to the
 *  row's home; totals never swap), or null when nothing exact exists. */
export function resolveLineReferenceView(
  event: ButterflyEventIdentity,
  kind: 'spread' | 'total',
  line: SpreadLine | TotalLine,
  readers: ButterflyReferenceReaders,
  period: string = 'full',
): OpFairView | null {
  const slice = resolveLineReferenceSlice(event, kind, line, readers, period);
  return slice ? buildOpFairView(slice, kind === 'spread' ? event.homeCode : null) : null;
}

/** One card owns one executable market and one exact reference lookup. No
 * center-line state, moneyline fallback, or comparison price enters execution. */
export function butterflyCardContext(
  event: ButterflyEventIdentity,
  kind: 'spread' | 'total',
  line: SpreadLine | TotalLine,
  readers: ButterflyReferenceReaders,
  period: string = 'full',
) {
  const book = line.kalshi ?? null;
  const spread = kind === 'spread' ? line as SpreadLine : null;
  const favorite = spread?.favored === 'away' ? event.awayCode : event.homeCode;
  const underdog = spread?.favored === 'away' ? event.homeCode : event.awayCode;
  const view = resolveLineReferenceView(event, kind, line, readers, period);
  const yesIsAway = kind === 'total' || spread?.favored === 'away';
  const referenceSide = (side: OpFairView['away'] | undefined) => ({
    tenths: side?.consensusTenths ?? side?.anchorFairTenths ?? null,
    source: side?.consensusTenths != null ? 'OP consensus' : side?.anchorFairTenths != null
      ? view?.anchorBookmaker ?? 'OP anchor' : null,
    updatedAtMs: side?.consensusTenths != null ? view?.consensusLastUpdateMs ?? null
      : side?.anchorFairTenths != null ? view?.anchorUpdatedAtMs ?? null : null,
  });
  return {
    key: JSON.stringify([event.key, period, kind, line.line, spread?.favored ?? null,
      kind === 'spread' ? spreadLineKey(line as SpreadLine) : totalLineKey(line as TotalLine)]),
    fairView: view, yesIsAway,
    gameKey: event.key, period, kind, strike: line.line, ticker: book?.marketTicker ?? null,
    book, availability: classifyLineAvailability(book),
    yesLabel: kind === 'total' ? `Over ${line.line}` : `${favorite} -${Math.abs(line.line)}`,
    noLabel: kind === 'total' ? `Under ${line.line}` : `${underdog} +${Math.abs(line.line)}`,
    reference: {
      yes: referenceSide(yesIsAway ? view?.away : view?.home),
      no: referenceSide(yesIsAway ? view?.home : view?.away),
      canPush: opLineCanPush(line.line),
      pinnacleWithdrawn: view?.pinnacleWithdrawn != null,
    },
    // Unobserved this session (ticker-scalar authority unconfirmed) → null,
    // which the panel already renders as a dash.
    volume: book != null && book.tickerSourceValidity !== 'unconfirmed' && Number.isFinite(book.volume) ? book.volume : null,
    volumeKind: book?.volumeIsDepthDerived ? 'Resting depth' : 'Traded volume',
    liquidity: book != null && Number.isFinite(book.liquidity) ? book.liquidity : null,
  };
}

export type ButterflyCardContext = ReturnType<typeof butterflyCardContext>;
